import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import {
  AlertConfiguration as AlertConfigType,
  AlertKind,
} from '../../types/dashboard';
import { apiService, ApiError } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import MultipleWebhooksManager from './MultipleWebhooksManager';
import { TRADE_BUILDER_CSS } from './tradeBuilder.styles';

/**
 * Redesigned guided trade builder for non-technical day traders.
 *
 * Handles both the "Two-Way Trade" (bidirectional_multi) and "Directional Trade"
 * (directional_multi) flows. Engineering vocabulary ("slot", "webhook", "token",
 * raw JSON) is replaced with friendlier wording ("indicator", "connection link",
 * "secure key"); technical bits are tucked behind "Advanced" and "Show other
 * formats" disclosures. A live preview on the right builds up as the trade is
 * defined. Backend wiring reuses the same apiService calls as AlertConfiguration.
 */

type IndicatorState = 'up' | 'down' | 'neutral';
type SourceType = 'webhook' | 'email' | 'ocr';
type Mode = 'twoway' | 'directional';

interface Indicator {
  name: string;
  source: SourceType;
  state: IndicatorState;
}

interface SlotCreds {
  tokenUp?: string | null;
  tokenDown?: string | null;
  token?: string | null;
}

interface TradeBuilderProps {
  alertKind: AlertKind;
  alert?: AlertConfigType;
  onSave: (
    alert: Omit<AlertConfigType, 'id'> & { id?: string },
    options?: { stayOnForm?: boolean }
  ) => void;
  onCancel: () => void;
}

const MARKET_CONDITIONS = [
  { value: 'Sideways/Ranging', label: 'Sideways / range' },
  { value: 'Breakout', label: 'Breakout' },
  { value: 'Reversal', label: 'Reversal' },
  { value: 'Other', label: 'Other' },
] as const;

const SOURCE_OPTIONS: { value: SourceType; label: string }[] = [
  { value: 'webhook', label: 'TradingView / charting alert (recommended)' },
  { value: 'email', label: 'Email alert' },
];

const SOURCE_LABEL: Record<SourceType, string> = {
  webhook: 'Charting alert',
  email: 'Email alert',
  ocr: 'Chart screenshot',
};

const STEPS = [
  { t: 'Define the trade', s: 'Symbol & basics' },
  { t: 'Define your indicators', s: 'What has to agree' },
  { t: 'Connect your indicators', s: 'Where signals come from' },
  { t: 'Connect execution', s: 'Where trades get sent' },
  { t: 'Review & activate', s: 'Check & go live' },
];

// Secret-word generator: avoids look-alike characters so it's easy to read and type.
function generateSecretWord(): string {
  const words = [
    'ACE', 'ARC', 'CAP', 'CAR', 'CAT', 'DEN', 'ERA', 'HAT', 'JET', 'KEY',
    'MAP', 'MAT', 'NET', 'PAN', 'RED', 'RUN', 'VAN', 'WAR', 'WAX', 'JADE',
    'CAFE', 'QUAD', 'WAVE', 'HAWK', 'DUKE', 'MANE', 'REEF', 'NEXT', 'CREW', 'TREK',
    'PEAR', 'DEAR', 'NEAR', 'CARE', 'DARK', 'DECK', 'DUNE', 'FERN', 'HARE', 'MARE',
    'PARK', 'PAVE', 'RAVE', 'TAME', 'WADE', 'WARM', 'CRANE', 'DANCE', 'HAVEN', 'RAVEN',
  ];
  const safeDigits = ['3', '4', '6', '7', '9'];
  const word = words[Math.floor(Math.random() * words.length)];
  const numPart = Array.from({ length: 3 }, () => safeDigits[Math.floor(Math.random() * safeDigits.length)]).join('');
  return `${word}${numPart}`;
}

function normalizeSource(raw: string | undefined): SourceType {
  const s = (raw ?? '').toLowerCase();
  if (s === 'webhook' || s === 'email' || s === 'ocr') return s;
  return 'webhook';
}

function buildTriggerUrl(token: string): string {
  return `${window.location.origin}/api/v1/incoming-webhook/trigger?token=${encodeURIComponent(token)}`;
}

function slotCredsFromApi(d: unknown): SlotCreds {
  if (!d || typeof d !== 'object') return {};
  const o = d as Record<string, unknown>;
  const pick = (a: string, b: string) => {
    const v = o[a] ?? o[b];
    if (v == null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };
  return {
    token: pick('token', 'token'),
    tokenUp: pick('tokenUp', 'token_up'),
    tokenDown: pick('tokenDown', 'token_down'),
  };
}

let fontsInjected = false;
function useBuilderFonts() {
  useEffect(() => {
    if (fontsInjected || typeof document === 'undefined') return;
    fontsInjected = true;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500&family=JetBrains+Mono:wght@400;500&display=swap';
    document.head.appendChild(link);
  }, []);
}

const Chevron: React.FC<{ open?: boolean }> = ({ open }) => (
  <span className={`chev${open ? ' open' : ''}`} aria-hidden>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 6 L15 12 L9 18" />
    </svg>
  </span>
);

const TradeBuilder: React.FC<TradeBuilderProps> = ({ alertKind, alert, onSave, onCancel }) => {
  const { token } = useAuth();
  useBuilderFonts();

  const mode: Mode = alertKind === 'directional_multi' ? 'directional' : 'twoway';
  const isTwoWay = mode === 'twoway';

  const alertId = useMemo(() => {
    if (!alert?.id) return null;
    const n = typeof alert.id === 'string' ? parseInt(alert.id, 10) : Number(alert.id);
    return Number.isNaN(n) ? null : n;
  }, [alert?.id]);

  const [step, setStep] = useState(0);
  const [maxIndicators, setMaxIndicators] = useState(3);

  const [symbol, setSymbol] = useState(alert?.stock_symbol || alert?.stockSymbol || '');
  const [name, setName] = useState(alert?.description || '');
  const [marketCondition, setMarketCondition] = useState<string>(() => {
    const pt = String(alert?.position_type || alert?.positionType || 'Sideways/Ranging');
    return MARKET_CONDITIONS.some((m) => m.value === pt) ? pt : 'Sideways/Ranging';
  });
  const [exitBehavior, setExitBehavior] = useState<'exit' | 'hold'>('exit');
  const [secretWord, setSecretWord] = useState(alert?.secret_word || alert?.secretWord || generateSecretWord());

  const [indicators, setIndicators] = useState<Indicator[]>(() => {
    const sources = (alert?.slot_sources?.length ? alert.slot_sources : ['webhook', 'webhook', 'webhook']) as string[];
    const descs = (alert as AlertConfigType & { slotDescriptions?: string[] })?.slot_descriptions
      ?? (alert as AlertConfigType & { slotDescriptions?: string[] })?.slotDescriptions
      ?? [];
    return sources.map((s, i) => ({
      name: descs[i] || `Indicator ${i + 1}`,
      source: normalizeSource(s),
      state: 'up' as IndicatorState,
    }));
  });

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [otherFormatsOpen, setOtherFormatsOpen] = useState<Record<number, boolean>>({});
  const [savedAlertId, setSavedAlertId] = useState<number | null>(alertId);
  const [isActive, setIsActive] = useState<boolean>(alert?.is_active ?? false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [slotCreds, setSlotCreds] = useState<Record<number, SlotCreds>>({});
  const [generating, setGenerating] = useState<Record<number, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const markDirty = useCallback(() => setDirty(true), []);
  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  // Subscription tier → how many indicators the plan allows.
  useEffect(() => {
    if (!token) return;
    apiService
      .getSubscription(token)
      .then((res) => {
        if (res.success && res.data?.max_trigger_count != null) {
          const n = Number(res.data.max_trigger_count);
          if (n >= 1) {
            // Cap at schema/UI ceiling (Ultimate = 10).
            const capped = Math.min(Math.floor(n), 10);
            setMaxIndicators(capped);
            setIndicators((prev) => {
              if (prev.length <= capped) return prev;
              return prev.slice(0, capped);
            });
          }
        }
      })
      .catch(() => {});
  }, [token]);

  const count = indicators.length;
  const countWord = count === 1 ? '1' : `all ${count}`;

  // ---- live preview computation ----------------------------------------
  const preview = useMemo(() => {
    const states = indicators.map((i) => i.state);
    const total = states.length;
    const ups = states.filter((s) => s === 'up').length;
    const downs = states.filter((s) => s === 'down').length;
    const active = states.filter((s) => s !== 'neutral').length;

    if (mode === 'directional') {
      if (total > 0 && ups === total) {
        return { armed: 'run' as const, gate: `all ${total} active → ready to trade` };
      }
      return { armed: 'exit' as const, gate: `not all active (${active} of ${total}) → exit if in a position` };
    }

    if (total > 0 && ups === total) {
      return { armed: 'long' as const, gate: `all ${total} agree bullish → ready to trade` };
    }
    if (total > 0 && downs === total) {
      return { armed: 'short' as const, gate: `all ${total} agree bearish → ready to trade` };
    }
    return { armed: 'exit' as const, gate: `not all agreeing (${ups} bullish / ${downs} bearish) → exit if in a position` };
  }, [indicators, mode]);

  // ---- indicator editing -----------------------------------------------
  const updateIndicator = (idx: number, patch: Partial<Indicator>) => {
    setIndicators((prev) => prev.map((ind, i) => (i === idx ? { ...ind, ...patch } : ind)));
    markDirty();
  };
  const addIndicator = () => {
    if (count >= maxIndicators) return;
    setIndicators((prev) => [...prev, { name: `Indicator ${prev.length + 1}`, source: 'webhook', state: 'up' }]);
    markDirty();
  };
  const removeIndicator = (idx: number) => {
    if (count <= 1) return;
    setIndicators((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  // ---- validation -------------------------------------------------------
  const validateBasics = (): boolean => {
    const next: Record<string, string> = {};
    if (!symbol.trim()) next.symbol = 'Enter the symbol this trade runs on.';
    else if (!/^[A-Za-z]{1,5}$/.test(symbol.trim())) next.symbol = 'Use 1–5 letters, e.g. SPY.';
    if (!secretWord.trim()) next.secretWord = 'A secure key is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ---- save -------------------------------------------------------------
  const save = async (opts: { activate?: boolean; advanceTo?: number; returnToList?: boolean } = {}): Promise<boolean> => {
    if (!validateBasics()) {
      notify('Please fix the highlighted fields before continuing.', 'error');
      setStep(0);
      return false;
    }
    const willActivate = opts.activate ?? isActive;
    const transformed: Record<string, unknown> = {
      stock_symbol: symbol.trim().toUpperCase(),
      position_type: marketCondition,
      secret_word: secretWord.trim(),
      webhook_url: '',
      is_active: willActivate,
      description: name.trim() || undefined,
      is_multi_trigger: true,
      trigger_count: count,
      slot_sources: indicators.map((i) => i.source),
      slot_descriptions: indicators.map((i) => i.name.trim()),
      alert_kind: alertKind,
    };

    setSaving(true);
    try {
      const res = savedAlertId
        ? await apiService.updateAlertConfiguration(String(savedAlertId), transformed, token || undefined)
        : await apiService.createAlertConfiguration(transformed, token || undefined);

      if (!res.success || !res.data) {
        notify(res.error || 'Could not save the trade. Please try again.', 'error');
        return false;
      }
      const saved = res.data as AlertConfigType;
      const idVal = saved.id != null ? String(saved.id) : undefined;
      const aid = idVal ? parseInt(idVal, 10) : null;
      if (aid && !Number.isNaN(aid)) {
        setSavedAlertId(aid);
      }
      setIsActive(willActivate);
      setDirty(false);

      onSave(
        {
          ...(idVal ? { id: idVal } : {}),
          user_id: saved.user_id ?? alert?.user_id ?? 1,
          tenant_id: saved.tenant_id ?? alert?.tenant_id ?? 'default',
          stock_symbol: transformed.stock_symbol as string,
          position_type: marketCondition as AlertConfigType['position_type'],
          secret_word: secretWord.trim(),
          webhook_url: '',
          is_active: willActivate,
          description: name.trim() || undefined,
          created_at: saved.created_at ?? alert?.created_at ?? '',
          alert_kind: alertKind,
          is_multi_trigger: true,
          trigger_count: count,
          slot_sources: indicators.map((i) => i.source),
          slot_descriptions: indicators.map((i) => i.name.trim()),
        },
        { stayOnForm: !opts.returnToList },
      );

      if (!opts.returnToList) {
        notify(
          willActivate ? 'Trade activated and saved.' : 'Saved. Your private connection links are ready below.',
          'success',
        );
      }
      if (opts.advanceTo != null) setStep(opts.advanceTo);
      return true;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? apiService.formatErrorDetail(e) || e.message
          : e instanceof Error
            ? e.message
            : 'Unexpected error while saving.';
      notify(msg, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ---- trigger links (per indicator) -----------------------------------
  const loadSlotCreds = useCallback(async () => {
    if (!token || !savedAlertId) return;
    for (let i = 0; i < indicators.length; i++) {
      if (indicators[i].source !== 'webhook') continue;
      try {
        const res = await apiService.getAlertSlotTriggerToken(savedAlertId, i, token);
        if (res.data) setSlotCreds((prev) => ({ ...prev, [i]: slotCredsFromApi(res.data) }));
      } catch {
        /* link not generated yet */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, savedAlertId, indicators.length]);

  const loadedFor = useRef<number | null>(null);
  useEffect(() => {
    if (savedAlertId && step === 2 && loadedFor.current !== savedAlertId) {
      loadedFor.current = savedAlertId;
      void loadSlotCreds();
    }
  }, [savedAlertId, step, loadSlotCreds]);

  const generateLink = async (idx: number) => {
    if (!token || !savedAlertId) {
      notify('Save the trade first to create connection links.', 'warning');
      return;
    }
    setGenerating((prev) => ({ ...prev, [idx]: true }));
    try {
      const res = await apiService.generateAlertSlotTriggerToken(savedAlertId, idx, token);
      const creds = slotCredsFromApi(res.data);
      setSlotCreds((prev) => ({ ...prev, [idx]: creds }));
      notify(`Connection link${creds.tokenUp && creds.tokenDown ? 's' : ''} ready for ${indicators[idx].name}.`, 'success');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not create the connection link.';
      notify(msg, 'error');
    } finally {
      setGenerating((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const copy = async (text: string, label = 'Copied to clipboard') => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      notify(label, 'success');
    } catch {
      notify('Could not copy. Select the text and copy manually.', 'error');
    }
  };

  const markerFor = (idx: number, dir?: 'up' | 'down' | 'out') => {
    const base = `${symbol.trim()} ${marketCondition} ${secretWord.trim()}`.toUpperCase();
    const suffix = dir ? `slot-${idx + 1}-${dir}` : `slot-${idx + 1}`;
    return `#Z# ${base} ${suffix} #Z#`;
  };

  // ---- navigation -------------------------------------------------------
  const goNext = async () => {
    if (step === 0) {
      if (!validateBasics()) {
        notify('Please fix the highlighted fields.', 'error');
        return;
      }
      setStep(1);
    } else if (step === 1) {
      // Save to mint the alert + connection links, then move to "Connect your indicators".
      await save({ advanceTo: 2 });
    } else if (step < STEPS.length - 1) {
      // Step 4 (Connect execution): warn if no destinations are set up, since the
      // trade won't send any orders when the indicators agree.
      if (step === 3 && savedAlertId) {
        let hasDestinations = true;
        try {
          const res = await apiService.getAlertWebhooks(savedAlertId, token || undefined);
          hasDestinations = res.success && Array.isArray(res.data) && res.data.length > 0;
        } catch {
          // If we can't verify, don't block the user.
          hasDestinations = true;
        }
        if (!hasDestinations) {
          const proceed = window.confirm(
            "You haven't added any execution destinations yet. Without one, this trade won't send orders anywhere when your indicators agree.\n\nContinue without a destination?"
          );
          if (!proceed) return;
        }
      }
      if (dirty) await save({ advanceTo: step + 1 });
      else setStep(step + 1);
    }
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const canVisit = (target: number) => target <= 1 || savedAlertId != null;

  const finalSave = async () => {
    await save({ activate: true, returnToList: true });
  };

  const tradeNoun = isTwoWay ? 'Two-Way Trade' : 'Directional Trade';

  // ---------------------------------------------------------------------
  return (
    <div className="twt-root">
      <style>{TRADE_BUILDER_CSS}</style>
      <div className="twt-inner">
        <div className="twt-page-head">
          <h1 className="twt-page-title">
            New <em>{tradeNoun}</em>
          </h1>
          <p className="twt-page-sub">
            {isTwoWay
              ? 'Trades both directions from one setup. Goes long when your indicators all agree bullish, short when they all agree bearish, and exits the moment they stop agreeing.'
              : 'Fires one trade when your indicators all line up, and exits the moment they stop agreeing.'}
          </p>
        </div>

        {/* breadcrumb stepper */}
        <div className="twt-stepper" role="tablist" aria-label="Trade setup steps">
          {STEPS.map((s, i) => (
            <button
              key={s.t}
              type="button"
              className={`twt-step-item${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}
              onClick={() => canVisit(i) && setStep(i)}
              disabled={!canVisit(i)}
              role="tab"
              aria-selected={i === step}
            >
              <span className="twt-step-num">{i < step ? '✓' : i + 1}</span>
              <span className="twt-step-label">
                <span className="t">{s.t}</span>
                <span className="s">{s.s}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="twt-grid">
          {/* LEFT — step content */}
          <div className="twt-builder">
            {step === 0 && renderStep1()}
            {step === 1 && renderStep2()}
            {step === 2 && renderStep3()}
            {step === 3 && renderStep4()}
            {step === 4 && renderStep5()}
          </div>

          {/* RIGHT — live preview rail */}
          <div className="twt-rail">{renderPreview()}</div>
        </div>

        {/* action bar */}
        <div className="twt-actionbar">
          <div className="twt-action-status">
            <span className={`sd${savedAlertId ? ' saved' : ''}`} />
            {savedAlertId
              ? isActive
                ? 'Active · live'
                : 'Saved as draft · not yet live'
              : 'Draft — not saved. Saving creates your private connection links.'}
          </div>
          <div className="twt-action-btns">
            <button type="button" className="twt-btn" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            {step > 0 && (
              <button type="button" className="twt-btn" onClick={goBack} disabled={saving}>
                Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" className="twt-btn primary" onClick={goNext} disabled={saving}>
                {saving ? 'Saving…' : step === 1 ? 'Save & continue →' : 'Continue →'}
              </button>
            ) : (
              <button type="button" className="twt-btn primary" onClick={finalSave} disabled={saving}>
                {saving ? 'Saving…' : isActive ? 'Save changes' : 'Activate trade'}
              </button>
            )}
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );

  // ===================================================================
  // STEP 1 — Define the trade
  // ===================================================================
  function renderStep1() {
    return (
      <>
        <div className="twt-panel">
          <div className="twt-eyebrow">Step 1 · In plain English</div>
          <div className="twt-panel-title">What should this trade do?</div>
          <p className="twt-panel-help">
            Read it like a sentence. Tap any highlighted word to change it. The preview on the right updates as you go.
          </p>
          <div className="twt-sentence">
            Watch{' '}
            <span className="twt-token has-select">
              {symbol.trim().toUpperCase() || 'SPY'}
              <select
                value={symbol.trim().toUpperCase()}
                onChange={(e) => {
                  setSymbol(e.target.value);
                  markDirty();
                }}
              >
                {['SPY', 'QQQ', 'AAPL', 'TSLA', 'NVDA'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                {symbol.trim() && !['SPY', 'QQQ', 'AAPL', 'TSLA', 'NVDA'].includes(symbol.trim().toUpperCase()) && (
                  <option value={symbol.trim().toUpperCase()}>{symbol.trim().toUpperCase()}</option>
                )}
              </select>
            </span>{' '}
            {isTwoWay ? (
              <>
                and trade both ways: go <span className="twt-token long">long</span> when{' '}
                <span className="twt-token">{countWord}</span> indicators agree bullish, go{' '}
                <span className="twt-token short">short</span> when they all agree bearish, and{' '}
              </>
            ) : (
              <>
                and run your trade when <span className="twt-token">{countWord}</span> indicators line up, then{' '}
              </>
            )}
            <span className="twt-token has-select">
              {exitBehavior === 'exit' ? 'exit' : 'hold & wait'}
              <select
                value={exitBehavior}
                onChange={(e) => {
                  setExitBehavior(e.target.value as 'exit' | 'hold');
                  markDirty();
                }}
              >
                <option value="exit">exit the position</option>
                <option value="hold">hold &amp; wait</option>
              </select>
            </span>{' '}
            the moment any one of them disagrees.
          </div>
        </div>

        <div className="twt-panel">
          <div className="twt-eyebrow">Step 1 · The basics</div>
          <div className="twt-panel-title">Name it & pick the symbol</div>
          <p className="twt-panel-help">Name it so you can find it later, and choose the symbol it runs on.</p>

          <div className="twt-field-row two">
            <div>
              <label className="twt-label" htmlFor="twt-name">
                Trade name
              </label>
              <input
                id="twt-name"
                className="twt-input"
                value={name}
                placeholder="e.g. SPY 9-min mean reversion"
                onChange={(e) => {
                  setName(e.target.value);
                  markDirty();
                }}
              />
              <div className="twt-hint">Shows in your list. Helps tell similar trades apart.</div>
            </div>
            <div>
              <label className="twt-label" htmlFor="twt-symbol">
                Symbol
              </label>
              <input
                id="twt-symbol"
                className={`twt-input${errors.symbol ? ' error' : ''}`}
                value={symbol}
                placeholder="e.g. AAPL"
                onChange={(e) => {
                  setSymbol(e.target.value.toUpperCase());
                  markDirty();
                }}
              />
              <div className={`twt-hint${errors.symbol ? ' err' : ''}`}>
                {errors.symbol || 'The ticker this trade runs on.'}
              </div>
            </div>
          </div>

          <div className="twt-field-row">
            <label className="twt-label" htmlFor="twt-cond">
              Market condition <span className="opt">— optional, tags the setup</span>
            </label>
            <select
              id="twt-cond"
              className="twt-input"
              style={{ maxWidth: 320 }}
              value={marketCondition}
              onChange={(e) => {
                setMarketCondition(e.target.value);
                markDirty();
              }}
            >
              {MARKET_CONDITIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <div className="twt-hint">
              Lets you run different setups separately on the same symbol (e.g. a range setup vs. a breakout).
            </div>
          </div>
        </div>
      </>
    );
  }

  // ===================================================================
  // STEP 2 — Define your indicators
  // ===================================================================
  function renderStep2() {
    const stateButtons: { v: IndicatorState; label: string }[] = isTwoWay
      ? [
          { v: 'up', label: 'Bullish' },
          { v: 'neutral', label: 'Neutral' },
          { v: 'down', label: 'Bearish' },
        ]
      : [
          { v: 'up', label: 'Active' },
          { v: 'neutral', label: 'Off' },
        ];
    return (
      <div className="twt-panel">
        <div className="twt-eyebrow">Step 2 · Your indicators</div>
        <div className="twt-panel-title">Which indicators have to agree?</div>
        <p className="twt-panel-help">
          Add the indicators that drive this trade. The trade only fires when <b>{countWord}</b> of them{' '}
          {isTwoWay ? 'agree on the same direction' : 'are active at once'}. Name each one so you remember what it is.
        </p>

        <div className="twt-ind-head">
          <span className="lbl">
            Your indicators — <span>{countWord}</span> must agree to trade
          </span>
        </div>

        {indicators.map((ind, i) => (
          <div className="twt-ind-card" key={i}>
            <div className="twt-ind-card-head">
              <span className="twt-badge">Indicator {i + 1}</span>
              <input
                className="twt-ind-name"
                value={ind.name}
                placeholder="Name this indicator"
                onChange={(e) => updateIndicator(i, { name: e.target.value })}
              />
              {count > 1 && (
                <button className="twt-remove" title="Remove indicator" onClick={() => removeIndicator(i)}>
                  ×
                </button>
              )}
            </div>
            <div className="twt-ind-grid">
              <div>
                <span className="twt-mini-label">Comes from</span>
                <select
                  className="twt-input"
                  value={ind.source}
                  onChange={(e) => updateIndicator(i, { source: e.target.value as SourceType })}
                >
                  {SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <span className="twt-mini-label">Right now (preview)</span>
                <div className="twt-seg">
                  {stateButtons.map(({ v, label }) => (
                    <button
                      key={v}
                      data-v={v}
                      className={ind.state === v ? 'on' : ''}
                      onClick={() => updateIndicator(i, { state: v })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        <button className="twt-add" onClick={addIndicator} disabled={count >= maxIndicators}>
          + Add another indicator{' '}
          <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(your plan allows up to {maxIndicators})</span>
        </button>

        <div className="twt-disclosure">
          <button
            type="button"
            className={`twt-disc-toggle${advancedOpen ? ' open' : ''}`}
            onClick={() => setAdvancedOpen((o) => !o)}
          >
            <Chevron open={advancedOpen} />
            Advanced options
          </button>
          {advancedOpen && (
            <div className="twt-disc-body">
              <div className="twt-field-row" style={{ marginTop: 4 }}>
                <label className="twt-label">
                  Secure key <span className="opt">— keeps your connection links private</span>
                </label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    className="twt-input"
                    value={secretWord}
                    readOnly
                    style={{ fontFamily: 'var(--mono)', fontSize: 13, maxWidth: 240 }}
                  />
                  <button
                    className="twt-btn"
                    style={{ padding: '9px 14px', fontSize: 13 }}
                    onClick={() => {
                      setSecretWord(generateSecretWord());
                      markDirty();
                    }}
                  >
                    ↻ Regenerate
                  </button>
                </div>
                <div className="twt-hint">
                  You don't need to change this. It stops anyone without your private link from triggering the trade.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===================================================================
  // STEP 3 — Connect your indicators
  // ===================================================================
  function renderStep3() {
    return (
      <div className="twt-panel">
        <div className="twt-eyebrow">Step 3 · Connect your indicators</div>
        <div className="twt-panel-title">Tell each indicator how to reach Branch Router</div>
        <p className="twt-panel-help">
          {isTwoWay ? (
            <>
              For each indicator, set up two alerts in your charting platform — one for when it turns{' '}
              <b>bullish</b>, one for when it turns <b>bearish</b> — and point them at the links below.
            </>
          ) : (
            <>
              For each indicator, set up an alert in your charting platform for when it <b>activates</b>, and point it
              at the link below.
            </>
          )}
        </p>

        {!savedAlertId && (
          <div className="twt-notice">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8 v0.01 M12 11 v5" />
            </svg>
            <p>Save the trade first (use Continue) to generate your private connection links.</p>
          </div>
        )}

        {savedAlertId &&
          indicators.map((ind, i) => {
            if (ind.source !== 'webhook') {
              return (
                <div className="twt-conn-block" key={i}>
                  <div className="twt-conn-title">
                    <span className="twt-badge">Indicator {i + 1}</span> {ind.name}
                  </div>
                  <p className="twt-conn-step">
                    This indicator comes from <b>{SOURCE_LABEL[ind.source]}</b>. Put{' '}
                    {isTwoWay ? 'these markers' : 'this marker'} in the{' '}
                    {ind.source === 'email' ? 'email subject or body' : 'chart text'}:
                  </p>
                  {isTwoWay ? (
                    <>
                      <div className="twt-marker-line">
                        <span className="ml-dir up">BULLISH</span>
                        <code>{markerFor(i, 'up')}</code>
                        <button className="twt-copy" onClick={() => copy(markerFor(i, 'up'), 'Bullish marker copied')}>
                          Copy
                        </button>
                      </div>
                      <div className="twt-marker-line">
                        <span className="ml-dir down">BEARISH</span>
                        <code>{markerFor(i, 'down')}</code>
                        <button className="twt-copy" onClick={() => copy(markerFor(i, 'down'), 'Bearish marker copied')}>
                          Copy
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="twt-marker-line">
                        <span className="ml-dir up">ACTIVATE</span>
                        <code>{markerFor(i)}</code>
                        <button className="twt-copy" onClick={() => copy(markerFor(i), 'Marker copied')}>
                          Copy
                        </button>
                      </div>
                      <div className="twt-marker-line">
                        <span className="ml-dir down">TURN OFF</span>
                        <code>{markerFor(i, 'out')}</code>
                        <button className="twt-copy" onClick={() => copy(markerFor(i, 'out'), 'Turn-off marker copied')}>
                          Copy
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            }

            const creds = slotCreds[i];
            const upUrl = creds?.tokenUp ? buildTriggerUrl(creds.tokenUp) : '';
            const downUrl = creds?.tokenDown ? buildTriggerUrl(creds.tokenDown) : '';
            const singleUrl = creds?.token ? buildTriggerUrl(creds.token) : '';
            const hasLinks = !!(upUrl || downUrl || singleUrl);
            const open = !!otherFormatsOpen[i];

            return (
              <div className="twt-conn-block" key={i}>
                <div className="twt-conn-title">
                  <span className="twt-badge">Indicator {i + 1}</span> {ind.name}
                </div>

                {!hasLinks ? (
                  <>
                    <p className="twt-conn-step">No connection links yet for this indicator.</p>
                    <button className="twt-btn primary" onClick={() => generateLink(i)} disabled={!!generating[i]}>
                      {generating[i] ? 'Creating…' : 'Create connection links'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="twt-conn-step">
                      <b>1.</b> Paste the matching link into your charting alert's webhook field:
                    </p>
                    {isTwoWay && upUrl && (
                      <div className="twt-url-line">
                        <span className="twt-badge" style={{ color: 'var(--green)', borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.1)' }}>
                          BULLISH
                        </span>
                        <code>{upUrl}</code>
                        <button className="twt-copy" onClick={() => copy(upUrl, 'Bullish link copied')}>
                          Copy
                        </button>
                      </div>
                    )}
                    {isTwoWay && downUrl && (
                      <div className="twt-url-line">
                        <span className="twt-badge" style={{ color: 'var(--red)', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)' }}>
                          BEARISH
                        </span>
                        <code>{downUrl}</code>
                        <button className="twt-copy" onClick={() => copy(downUrl, 'Bearish link copied')}>
                          Copy
                        </button>
                      </div>
                    )}
                    {(!isTwoWay || (!upUrl && !downUrl)) && (upUrl || singleUrl) && (
                      <div className="twt-url-line">
                        {!isTwoWay && (
                          <span className="twt-badge" style={{ color: 'var(--green)', borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.1)' }}>
                            ACTIVATE
                          </span>
                        )}
                        <code>{upUrl || singleUrl}</code>
                        <button className="twt-copy" onClick={() => copy(upUrl || singleUrl, 'Link copied')}>
                          Copy
                        </button>
                      </div>
                    )}
                    {isTwoWay ? (
                      <p className="twt-conn-step" style={{ marginTop: 8 }}>
                        <b>2.</b> That's it — leave the alert message empty. Re-create the links anytime if you need
                        to rotate them.
                      </p>
                    ) : (
                      <>
                        <p className="twt-conn-step" style={{ marginTop: 8 }}>
                          <b>2.</b> On your <b>activate</b> alert, leave the message empty.
                        </p>
                        <p className="twt-conn-step" style={{ marginTop: 8 }}>
                          <b>3.</b> To <b>turn this indicator off</b> (exit when it stops agreeing), add a second
                          charting alert with the <b>same link</b> and this message:
                        </p>
                        {(upUrl || singleUrl) && (
                          <div className="twt-url-line">
                            <span className="twt-badge" style={{ color: 'var(--amber, #f59e0b)', borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.1)' }}>
                              TURN OFF
                            </span>
                            <code>{upUrl || singleUrl}</code>
                            <button className="twt-copy" onClick={() => copy(upUrl || singleUrl, 'Link copied')}>
                              Copy
                            </button>
                          </div>
                        )}
                        <div className="twt-json-box warn" style={{ marginTop: 6 }}>
                          <span className="jc">{'{ "deactivate": true }'}</span>
                          <button className="twt-copy" onClick={() => copy('{"deactivate":true}', 'Deactivate message copied')}>
                            Copy
                          </button>
                        </div>
                        <p className="twt-conn-step" style={{ marginTop: 8 }}>
                          Re-create the link anytime if you need to rotate it.
                        </p>
                      </>
                    )}
                    <button
                      className="twt-btn"
                      style={{ padding: '7px 14px', fontSize: 13, marginTop: 4 }}
                      onClick={() => generateLink(i)}
                      disabled={!!generating[i]}
                    >
                      {generating[i] ? 'Working…' : '↻ Re-create links'}
                    </button>

                    <div className="twt-disclosure">
                      <button
                        type="button"
                        className={`twt-disc-toggle${open ? ' open' : ''}`}
                        onClick={() => setOtherFormatsOpen((o) => ({ ...o, [i]: !o[i] }))}
                      >
                        <Chevron open={open} />
                        Show other formats
                      </button>
                      {open && (
                        <div className="twt-disc-body">
                          {isTwoWay && (
                            <>
                              <div className="twt-fmt-label">One webhook only? Use this link and a message instead</div>
                              {(upUrl || singleUrl) && (
                                <div className="twt-url-line">
                                  <code>{upUrl || singleUrl}</code>
                                  <button className="twt-copy" onClick={() => copy(upUrl || singleUrl, 'Link copied')}>
                                    Copy
                                  </button>
                                </div>
                              )}
                              <div className="twt-json-pair">
                                <div>
                                  <div className="twt-json-lead up">On your bullish alert</div>
                                  <div className="twt-json-box">
                                    <span className="jc">{'{ "direction": "up" }'}</span>
                                    <button className="twt-copy" onClick={() => copy('{"direction":"up"}', 'Copied')}>
                                      Copy
                                    </button>
                                  </div>
                                </div>
                                <div>
                                  <div className="twt-json-lead down">On your bearish alert</div>
                                  <div className="twt-json-box down">
                                    <span className="jc">{'{ "direction": "down" }'}</span>
                                    <button className="twt-copy" onClick={() => copy('{"direction":"down"}', 'Copied')}>
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          <div className="twt-fmt-label">Email — paste these lines instead</div>
                          {isTwoWay ? (
                            <>
                              <div className="twt-marker-line">
                                <span className="ml-dir up">BULLISH</span>
                                <code>{markerFor(i, 'up')}</code>
                                <button className="twt-copy" onClick={() => copy(markerFor(i, 'up'), 'Copied')}>
                                  Copy
                                </button>
                              </div>
                              <div className="twt-marker-line">
                                <span className="ml-dir down">BEARISH</span>
                                <code>{markerFor(i, 'down')}</code>
                                <button className="twt-copy" onClick={() => copy(markerFor(i, 'down'), 'Copied')}>
                                  Copy
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="twt-marker-line">
                                <span className="ml-dir up">ACTIVATE</span>
                                <code>{markerFor(i)}</code>
                                <button className="twt-copy" onClick={() => copy(markerFor(i), 'Copied')}>
                                  Copy
                                </button>
                              </div>
                              <div className="twt-marker-line">
                                <span className="ml-dir down">TURN OFF</span>
                                <code>{markerFor(i, 'out')}</code>
                                <button className="twt-copy" onClick={() => copy(markerFor(i, 'out'), 'Copied')}>
                                  Copy
                                </button>
                              </div>
                            </>
                          )}

                          {isTwoWay && (
                            <>
                              <div className="twt-fmt-label">Reset this indicator to neutral</div>
                              <div className="twt-json-box warn">
                                <span className="jc">{'{ "deactivate": true }'}</span>
                                <button className="twt-copy" onClick={() => copy('{"deactivate":true}', 'Copied')}>
                                  Copy
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  // ===================================================================
  // STEP 4 — Connect execution
  // ===================================================================
  function renderStep4() {
    return (
      <div className="twt-panel">
        <div className="twt-eyebrow">Step 4 · Connect execution</div>
        <div className="twt-panel-title">Where should trades be sent?</div>
        <p className="twt-panel-help">
          {isTwoWay ? (
            <>
              Add the destination(s) Branch Router sends orders to when your indicators agree — one for going{' '}
              <b>long</b>, one for going <b>short</b>, and one to <b>exit</b> when they stop agreeing.
            </>
          ) : (
            <>
              Add destinations for when all indicators line up to <b>enter</b>, and destinations for the one{' '}
              <b>exit</b> that runs when any indicator says exit. You can add more than one destination for each.
            </>
          )}
        </p>

        {savedAlertId ? (
          <>
            <MultipleWebhooksManager
              embedded
              alertId={savedAlertId}
              alertName={name.trim() || `${symbol.trim().toUpperCase()} — ${marketCondition}`}
              alertKind={alertKind}
              triggerCount={mode === 'directional' ? count : undefined}
            />
            <p className="twt-conn-step" style={{ marginTop: 14 }}>
              Set the {isTwoWay ? 'long, short, and exit' : 'entry and exit'} destinations here. You can also add extra
              fields for your broker on each destination.
            </p>
          </>
        ) : (
          <div className="twt-notice">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8 v0.01 M12 11 v5" />
            </svg>
            <p>Save the trade first to add execution destinations.</p>
          </div>
        )}
      </div>
    );
  }

  // ===================================================================
  // STEP 5 — Review & activate
  // ===================================================================
  function renderStep5() {
    return (
      <div className="twt-panel">
        <div className="twt-eyebrow">Step 5 · Review & activate</div>
        <div className="twt-panel-title">Does this look right?</div>
        <p className="twt-panel-help">
          Give it a final check. When you activate, Branch Router starts watching your indicators and sending trades the
          moment they agree.
        </p>

        <div className="twt-review-row">
          <span className="k">Trade type</span>
          <span className="v">{tradeNoun}</span>
        </div>
        <div className="twt-review-row">
          <span className="k">Trade name</span>
          <span className="v">{name.trim() || '—'}</span>
        </div>
        <div className="twt-review-row">
          <span className="k">Symbol</span>
          <span className="v">{symbol.trim().toUpperCase() || '—'}</span>
        </div>
        <div className="twt-review-row">
          <span className="k">Market condition</span>
          <span className="v">{MARKET_CONDITIONS.find((m) => m.value === marketCondition)?.label ?? marketCondition}</span>
        </div>
        <div className="twt-review-row">
          <span className="k">Indicators</span>
          <span className="v">
            {count} · {indicators.map((x) => x.name).join(', ')}
          </span>
        </div>
        <div className="twt-review-row">
          <span className="k">Rule</span>
          <span className="v">
            {countWord} must {isTwoWay ? 'agree' : 'be active'} to trade
          </span>
        </div>
        <div className="twt-review-row">
          <span className="k">When they stop agreeing</span>
          <span className="v">{exitBehavior === 'exit' ? 'Exit the position' : 'Hold & wait'}</span>
        </div>
        <div className="twt-review-row">
          <span className="k">Status</span>
          <span className="v" style={{ color: isActive ? 'var(--green)' : 'var(--amber)' }}>
            {isActive ? 'Active' : 'Draft'}
          </span>
        </div>

        <p className="twt-panel-help" style={{ margin: '20px 0 0' }}>
          Use <b>{isActive ? 'Save changes' : 'Activate trade'}</b> below to finish. You can pause it anytime from your
          alert list.
        </p>
      </div>
    );
  }

  // ===================================================================
  // RIGHT RAIL — live preview
  // ===================================================================
  function renderPreview() {
    const sym = symbol.trim().toUpperCase() || 'SPY';
    const stMap: Record<IndicatorState, string> = isTwoWay
      ? { up: 'BULLISH', down: 'BEARISH', neutral: 'NEUTRAL' }
      : { up: 'ACTIVE', down: 'ACTIVE', neutral: 'OFF' };
    return (
      <div className="twt-diagram">
        <div className="twt-diagram-head">
          <span className="t">Live preview · {sym}</span>
          <span className="twt-live">
            <span className="twt-live-dot" />
            UPDATES AS YOU BUILD
          </span>
        </div>
        <div className="twt-diagram-sub">
          Trades <span>only when</span> {countWord} {isTwoWay ? 'agree' : 'are active'}
        </div>

        <div>
          {indicators.map((ind, i) => (
            <div className="twt-dg-slot" key={i}>
              <span className={`twt-dg-dot ${ind.state}`} />
              <span className="nm">{ind.name || `Indicator ${i + 1}`}</span>
              <span className={`st ${ind.state}`}>{stMap[ind.state]}</span>
            </div>
          ))}
        </div>

        <div className="twt-gate">
          <span
            style={{
              color:
                preview.armed === 'long' || preview.armed === 'run'
                  ? 'var(--green)'
                  : preview.armed === 'short'
                  ? 'var(--red)'
                  : 'var(--amber)',
            }}
          >
            {preview.gate}
          </span>
        </div>

        <div className="twt-outcomes">
          {isTwoWay ? (
            <>
              {renderOutcome('long', preview.armed === 'long', 'Go', 'long', 'all agree bullish', 'M5 15 L12 8 L19 15')}
              {renderOutcome('short', preview.armed === 'short', 'Go', 'short', 'all agree bearish', 'M5 9 L12 16 L19 9')}
            </>
          ) : (
            renderOutcome('long', preview.armed === 'run', 'Run the', 'trade', 'all indicators active', 'M5 15 L12 8 L19 15')
          )}
          <div className={`twt-outcome exit${preview.armed === 'exit' ? ' armed' : ''}`}>
            <div className="o-label">
              <div className="o-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12 H19" />
                </svg>
              </div>
              <div>
                <div>
                  They stop agreeing → <strong>exit</strong>
                </div>
                <div className="o-route">{exitBehavior === 'exit' ? 'close the position' : 'hold & wait'}</div>
              </div>
            </div>
            <span className="twt-o-tag" style={{ color: preview.armed === 'exit' ? 'var(--amber)' : 'var(--muted)' }}>
              {preview.armed === 'exit' ? '● FIRES' : 'idle'}
            </span>
          </div>
        </div>

        <div className="twt-diagram-foot">
          <strong>Reading this:</strong> when {countWord} indicators {isTwoWay ? 'agree bullish' : 'are active'}, the{' '}
          <span style={{ color: 'var(--green)' }}>{isTwoWay ? 'long' : 'entry'}</span> trade is ready. Flip any one{' '}
          {isTwoWay ? 'to neutral or bearish' : 'off'} and they no longer all agree — the{' '}
          <span style={{ color: 'var(--amber)' }}>exit</span> fires to close the position.
        </div>
      </div>
    );
  }

  function renderOutcome(
    cls: 'long' | 'short',
    armed: boolean,
    lead: string,
    strong: string,
    route: string,
    iconPath: string,
  ) {
    return (
      <div className={`twt-outcome ${cls}${armed ? ' armed' : ''}`}>
        <div className="o-label">
          <div className="o-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d={iconPath} />
            </svg>
          </div>
          <div>
            <div>
              {lead} <strong>{strong}</strong>
            </div>
            <div className="o-route">{route}</div>
          </div>
        </div>
        <span
          className="twt-o-tag"
          style={{ color: armed ? (cls === 'short' ? 'var(--red)' : 'var(--green)') : 'var(--muted)' }}
        >
          {armed ? '● ARMED' : 'idle'}
        </span>
      </div>
    );
  }
};

export default TradeBuilder;
